import { RestUserFeed } from '../../schema-copy/api-schema';
import { FeedCollection, Post } from '../../schema-copy/feed-schema';
import {
  CURRENT_BATCH_SIZE, downloadCurrentFiles, downloadFeedFile, FeedFileNotFoundError, feedFileUrl, getArchiveNameForIndex,
  getCurrentNameForIndex, getFirstCurrent, isCurrentFromInstance,
} from '../post-engine';
import { getFeedIconUrl } from '../../schema-copy/feed-icon';
import {
  deleteFeed, deletePosts, dequeueImages, getImageQueue, getStoredFeedIds, getStoredIconUrls, getStoredImageUrls,
  getStoredIndices, getStoredPost, putIcon, putImage, putPosts,
} from './post-store';
import { downloadImage, imageKeyUrl, ImageUnavailableError, postImageUrls } from './post-images';
import { HostQueue } from './host-queue';
import { ThrottledError, throttleKey } from '../host-throttle';

/** Posts this far below a feed's postCount are not kept (the read-marker window, MAX_TRACKED_POSTS).
 *  Starred and read-later posts are kept regardless. */
export const OFFLINE_POST_WINDOW = 1000;
const CONCURRENCY = 4;
/** Requests at once to one domain: a single site doesn't get all of them (see host-throttle.ts). */
const PER_DOMAIN_CONCURRENCY = 2;
/** Width of the stored feed icon; FeedIcon at any size is served from it while offline. */
const ICON_WIDTH = 96;

/** What the downloader needs from the feed engine. */
export interface OfflinePostSource {
  userId: string;
  getSubscribedFeeds(): RestUserFeed[];
  /** Any known feed, including unsubscribed ones that saved posts belong to. */
  getFeed(feedId: string): RestUserFeed | undefined;
  getUnreadIndices(feedId: string): number[];
  /** Starred and read-later entries, "feedId-postIndex". */
  getSavedEntries(): string[];
}

// --- progress (shown on the Preferences page) ---

export interface OfflineDownloadProgress {
  /** A download run is in progress. */
  active: boolean;
  /** Files downloaded / planned in the current (or last) run. */
  done: number;
  total: number;
  /** Files that failed in the current (or last) run; they are retried by the next run. */
  failed: number;
  /** Images (post images and feed icons) stored / to download in the current (or last) run. */
  imagesDone: number;
  imagesTotal: number;
  /** Images that could not be stored in the current (or last) run. */
  imagesFailed: number;
}

const IDLE: OfflineDownloadProgress = { active: false, done: 0, total: 0, failed: 0, imagesDone: 0, imagesTotal: 0, imagesFailed: 0 };
let progress = IDLE;
const progressListeners = new Set<(p: OfflineDownloadProgress) => void>();

export function getOfflineDownloadProgress(): OfflineDownloadProgress {
  return progress;
}

export function onOfflineDownloadProgress(cb: (p: OfflineDownloadProgress) => void): () => void {
  progressListeners.add(cb);
  return () => progressListeners.delete(cb);
}

function setProgress(p: OfflineDownloadProgress) {
  progress = p;
  progressListeners.forEach(cb => {
    try { cb(p); } catch (e) { console.error('Offline progress callback error:', e); }
  });
}

// --- downloader ---

interface FileJob {
  feed: RestUserFeed;
  fileName: string;
  /** Set for the current file (fetched by postCount, not by name). */
  currentPostCount?: number;
  /** The wanted posts this file should contain. */
  indices: number[];
  /** Which of the file's posts to store: the whole file, minus posts we don't keep. */
  keep: (index: number) => boolean;
}

/** Images to download for one post, or (without postIndex) a feed's icon. */
interface ImageJob {
  feedId: string;
  postIndex?: number;
  urls: string[];
  /** Some of the post's images were left out (failed earlier this session): keep it queued. */
  skipped?: boolean;
}

type Stats = Omit<OfflineDownloadProgress, 'active'>;

/** State of one download run, shared by its steps. */
interface RunContext {
  signal: AbortSignal;
  stats: Stats;
  publish: (active: boolean) => void;
  /** Every request of the run is a task here, throttled per domain. */
  queue: HostQueue;
}

/**
 * Keeps the posts needed for offline reading in the post store: per feed, the unread posts within
 * the last OFFLINE_POST_WINDOW, plus every starred and read-later post. Whole files are downloaded
 * and stored (archives hold 20 posts, so some read posts come along). Posts that left the window,
 * and posts of feeds no longer subscribed (unless saved), are deleted.
 *
 * Runs in the background; sync() requests can arrive at any time and are merged into the run.
 */
export class OfflinePostDownloader {
  private controller: AbortController | null = null;
  private running: Promise<void> | null = null;
  private pendingAll = false;
  private pendingFeeds = new Set<string>();
  // Archive files the CDN doesn't have ("feedId/fileName"). Not requested again this session; their
  // posts show the offline stub.
  private missingFiles = new Set<string>();
  // Image URLs that failed this session; not requested again until the next full run.
  private failedImages = new Set<string>();

  constructor(private source: OfflinePostSource) {}

  /** Bring the stored posts up to date for the given feeds, or for all feeds. */
  sync(feedIds?: string[]): void {
    if (!feedIds) this.pendingAll = true;
    else feedIds.forEach(id => this.pendingFeeds.add(id));
    if (this.running) return;
    const run = this.run();
    this.running = run;
    run.finally(() => { if (this.running == run) this.running = null; });
  }

  /** Abort all downloads and forget pending requests. */
  stop(): void {
    this.controller?.abort();
    this.controller = null;
    this.running = null;
    this.pendingAll = false;
    this.pendingFeeds.clear();
    setProgress(IDLE);
  }

  /** Resolves when the current run (if any) is finished. For tests. */
  whenIdle(): Promise<void> {
    return this.running ?? Promise.resolve();
  }

  private async run(): Promise<void> {
    const controller = this.controller = new AbortController();
    const { signal } = controller;
    const stats: Stats = { done: 0, total: 0, failed: 0, imagesDone: 0, imagesTotal: 0, imagesFailed: 0 };
    const publish = (active: boolean) => { if (!signal.aborted) setProgress({ ...stats, active }); };
    const ctx: RunContext = { signal, stats, publish, queue: new HostQueue(signal, CONCURRENCY, PER_DOMAIN_CONCURRENCY) };
    // Let the caller finish the change that triggered sync() (e.g. adding a star) before planning.
    await Promise.resolve();
    try {
      while (!signal.aborted && (this.pendingAll || this.pendingFeeds.size)) {
        const feedIds = this.pendingAll ? undefined : Array.from(this.pendingFeeds);
        this.pendingAll = false;
        this.pendingFeeds.clear();
        if (!feedIds) this.failedImages.clear(); // a full run retries failed images
        const { files, icons } = await this.plan(feedIds, signal);
        stats.total += files.length;
        // Icons first: few, and visible everywhere. Each file's images are queued once its posts
        // are stored.
        icons.forEach(job => this.addImageJob(job, ctx));
        this.addFileJobs(files, ctx);
        publish(true);
        await ctx.queue.whenIdle();
        // Whatever is still queued: posts stored earlier, or whose images failed with a network error.
        await this.downloadQueuedImages(feedIds, ctx);
      }
    } catch (e) {
      if (!signal.aborted) console.error('Offline download run failed', e);
    } finally {
      publish(false);
      if (this.controller == controller) this.controller = null;
    }
  }

  /** Work out which files to download for these feeds (all when undefined), and delete posts that
   *  are no longer needed. */
  private async plan(feedIds: string[] | undefined, signal: AbortSignal): Promise<{ files: FileJob[], icons: ImageJob[] }> {
    const { userId } = this.source;
    const storedIcons = await getStoredIconUrls(userId);
    const icons: ImageJob[] = [];
    const saved = groupSavedEntries(this.source.getSavedEntries());
    const subscribed = new Map(this.source.getSubscribedFeeds().map(f => [f.id, f]));

    let ids = feedIds;
    if (!ids) {
      ids = Array.from(new Set([...Array.from(subscribed.keys()), ...Array.from(saved.keys())]));
      // Feeds stored earlier that are neither subscribed nor saved any more: free their space.
      for (const id of await getStoredFeedIds(userId))
        if (!subscribed.has(id) && !saved.has(id)) ids.push(id);
    }

    const jobs: FileJob[] = [];
    for (const feedId of ids) {
      if (signal.aborted) break;
      const savedIndices = saved.get(feedId) ?? new Set<number>();
      const isSubscribed = subscribed.has(feedId);
      const feed = subscribed.get(feedId) ?? this.source.getFeed(feedId);
      const postCount = feed?.postCount ?? 0;
      const minKept = postCount - OFFLINE_POST_WINDOW;
      const keep = (i: number) => savedIndices.has(i) || (isSubscribed && i >= minKept && i < postCount);

      // Neither subscribed nor saved: free everything of the feed, icon included.
      if (!isSubscribed && !savedIndices.size) {
        await deleteFeed(userId, feedId);
        continue;
      }
      const stored = await getStoredIndices(userId, feedId);
      await deletePosts(userId, feedId, Array.from(stored).filter(i => !keep(i)));
      if (!feed) continue;

      const iconUrl = getFeedIconUrl(feed, ICON_WIDTH);
      if (storedIcons.get(feedId) != imageKeyUrl(iconUrl) && !this.failedImages.has(iconUrl))
        icons.push({ feedId, urls: [iconUrl] });
      if (!postCount) continue;

      const wanted = new Set(Array.from(savedIndices).filter(i => i < postCount));
      if (isSubscribed)
        for (const i of this.source.getUnreadIndices(feedId))
          if (i >= minKept) wanted.add(i);
      const missing = Array.from(wanted).filter(i => !stored.has(i));

      const firstCurrent = getFirstCurrent(postCount);
      const byFile = new Map<string, number[]>();
      for (const i of missing) {
        const fileName = i >= firstCurrent ? getCurrentNameForIndex(postCount) : getArchiveNameForIndex(i);
        byFile.set(fileName, [...(byFile.get(fileName) ?? []), i]);
      }
      byFile.forEach((indices, fileName) => !this.missingFiles.has(`${feedId}/${fileName}`) && jobs.push({
        feed, fileName, indices, keep,
        currentPostCount: fileName == getCurrentNameForIndex(postCount) ? postCount : undefined,
      }));
    }
    return { files: jobs, icons };
  }

  /**
   * Download the images of stored posts that are still queued (only those of `feedIds` unless this is
   * a full run).
   */
  private async downloadQueuedImages(feedIds: string[] | undefined, ctx: RunContext): Promise<void> {
    const onlyFeeds = feedIds && new Set(feedIds);
    for (const [feedId, postIndex] of await getImageQueue(this.source.userId)) {
      if (ctx.signal.aborted) return;
      if (onlyFeeds && !onlyFeeds.has(feedId)) continue;
      const job = await this.postImageJob(feedId, postIndex);
      if (job) this.addImageJob(job, ctx);
    }
    ctx.publish(true);
    await ctx.queue.whenIdle();
  }

  /** The images still to download for a queued post; dequeues it when there are none. */
  private async postImageJob(feedId: string, postIndex: number): Promise<ImageJob | undefined> {
    const { userId } = this.source;
    const post = await getStoredPost(userId, feedId, postIndex);
    const stored = await getStoredImageUrls(userId, feedId, postIndex);
    const missing = post ? postImageUrls(post).filter(url => !stored.has(imageKeyUrl(url))) : [];
    if (!missing.length) {
      await dequeueImages(userId, feedId, postIndex);
      return undefined;
    }
    const urls = missing.filter(url => !this.failedImages.has(url));
    return urls.length ? { feedId, postIndex, urls, skipped: urls.length < missing.length } : undefined;
  }

  /** Queue a job's images, one task each. A post leaves the image queue once each of its images is
   *  stored or known to be unavailable; after a network failure, or when its domain stayed
   *  throttled, it stays queued for the next run. */
  private addImageJob(job: ImageJob, ctx: RunContext): void {
    const { userId } = this.source;
    const { signal, stats, publish, queue } = ctx;
    let pending = job.urls.length;
    let retryLater = !!job.skipped;
    const settle = async (retry: boolean) => {
      retryLater ||= retry;
      if (--pending || retryLater || job.postIndex == undefined || signal.aborted) return;
      await dequeueImages(userId, job.feedId, job.postIndex);
    };
    const fail = (url: string, retry: boolean) => {
      this.failedImages.add(url);
      stats.imagesFailed++;
      publish(true);
      return settle(retry);
    };
    stats.imagesTotal += job.urls.length;
    for (const url of job.urls)
      queue.add(throttleKey(url), async () => {
        if (signal.aborted) return;
        try {
          const image = await downloadImage(url, signal);
          if (signal.aborted) return;
          if (job.postIndex == undefined) await putIcon(userId, job.feedId, image);
          else await putImage(userId, job.feedId, job.postIndex, image);
          stats.imagesDone++;
          publish(true);
          await settle(false);
        } catch (e) {
          if (signal.aborted) return;
          if (e instanceof ThrottledError) throw e; // the queue retries it later
          await fail(url, !(e instanceof ImageUnavailableError));
        }
      }, () => fail(url, true));
  }

  /** Queue the file downloads. Current files that come from a sync instance are fetched in
   *  batches, one request per instance and CURRENT_BATCH_SIZE feeds. */
  private addFileJobs(jobs: FileJob[], ctx: RunContext): void {
    const byInstance = new Map<string, FileJob[]>();
    for (const job of jobs)
      if (job.currentPostCount != undefined && isCurrentFromInstance(job.feed))
        byInstance.set(job.feed.syncInstance!, [...(byInstance.get(job.feed.syncInstance!) ?? []), job]);
      else
        this.addFileJob(job, ctx);
    byInstance.forEach(list => {
      for (let i = 0; i < list.length; i += CURRENT_BATCH_SIZE)
        this.addCurrentBatch(list.slice(i, i + CURRENT_BATCH_SIZE), ctx);
    });
  }

  private addFileJob(job: FileJob, ctx: RunContext): void {
    const { signal, stats, publish } = ctx;
    ctx.queue.add(throttleKey(feedFileUrl(job.feed, job.fileName, job.currentPostCount)), async () => {
      if (signal.aborted) return;
      try {
        const collection = await downloadFeedFile(job.feed, job.fileName, signal, job.currentPostCount);
        if (signal.aborted) return;
        await this.storeFile(job, collection, ctx);
        stats.done++;
      } catch (e) {
        if (signal.aborted) return;
        if (e instanceof ThrottledError) throw e; // the queue retries it later
        if (e instanceof FeedFileNotFoundError) {
          // Nothing to retry: count it as handled.
          this.missingFiles.add(`${job.feed.id}/${job.fileName}`);
          stats.done++;
        } else {
          stats.failed++;
          console.warn(`Offline download of ${job.feed.id}/${job.fileName} failed`, e);
        }
      }
      publish(true);
    }, () => {
      stats.failed++;
      publish(true);
    });
  }

  /** Download the current files of feeds on one sync instance in one request. Files the batch
   *  didn't deliver (missing, behind, or the request failed) are downloaded one by one. */
  private addCurrentBatch(jobs: FileJob[], ctx: RunContext): void {
    const { signal, stats, publish } = ctx;
    const first = jobs[0];
    ctx.queue.add(throttleKey(feedFileUrl(first.feed, first.fileName, first.currentPostCount)), async () => {
      if (signal.aborted) return;
      let collections: (FeedCollection | undefined)[] = [];
      try {
        collections = await downloadCurrentFiles(jobs.map(job => ({ feed: job.feed, postCount: job.currentPostCount! })), signal);
      } catch (e) {
        if (signal.aborted) return;
        if (e instanceof ThrottledError) throw e;
        console.warn('Offline batch download of current files failed', e);
      }
      for (const [i, job] of Array.from(jobs.entries())) {
        if (signal.aborted) return;
        const collection = collections[i];
        if (!collection) {
          this.addFileJob(job, ctx);
          continue;
        }
        try {
          await this.storeFile(job, collection, ctx);
          stats.done++;
        } catch (e) {
          if (signal.aborted) return;
          stats.failed++;
          console.warn(`Offline download of ${job.feed.id}/${job.fileName} failed`, e);
        }
        publish(true);
      }
    }, () => {
      stats.failed += jobs.length;
      publish(true);
    });
  }

  /** Store a downloaded file's posts and queue their images. The current file can start later than
   *  the 20-post boundary (as in getPosts): the archives with the posts before its first one are
   *  queued as downloads of their own. */
  private async storeFile(job: FileJob, collection: FeedCollection, ctx: RunContext): Promise<void> {
    const { userId } = this.source;
    const kept = collection.posts.filter(p => job.keep(p.number));
    await putPosts(userId, job.feed.id, kept);
    for (const post of kept) {
      if (ctx.signal.aborted) return;
      const imageJob = await this.postImageJob(job.feed.id, post.number);
      if (imageJob) this.addImageJob(imageJob, ctx);
    }
    if (job.currentPostCount == undefined) return;

    const byArchive = new Map<string, number[]>();
    for (const i of job.indices.filter(i => i < collection.firstPost)) {
      const fileName = getArchiveNameForIndex(i);
      byArchive.set(fileName, [...(byArchive.get(fileName) ?? []), i]);
    }
    byArchive.forEach((indices, fileName) => {
      if (this.missingFiles.has(`${job.feed.id}/${fileName}`)) return;
      ctx.stats.total++;
      this.addFileJob({ feed: job.feed, fileName, indices, keep: job.keep }, ctx);
    });
  }
}

/** "feedId-postIndex" entries → postIndices by feedId. */
function groupSavedEntries(entries: string[]): Map<string, Set<number>> {
  const result = new Map<string, Set<number>>();
  for (const entry of entries) {
    const dash = entry.lastIndexOf('-');
    if (dash <= 0) continue;
    const feedId = entry.substring(0, dash);
    const index = Number(entry.substring(dash + 1));
    if (!Number.isInteger(index)) continue;
    if (!result.has(feedId)) result.set(feedId, new Set());
    result.get(feedId)!.add(index);
  }
  return result;
}
